import { useRef } from 'react';

export const BLEED = 14;

let _gidc = 0;

// A concave 4-point spark / diamond centred at (cx,cy) with tip radius s.
function sparkPath(cx, cy, s) {
  const k = s * 0.2;
  return `M ${cx} ${cy - s}`
    + ` C ${cx} ${cy - k} ${cx + k} ${cy} ${cx + s} ${cy}`
    + ` C ${cx + k} ${cy} ${cx} ${cy + k} ${cx} ${cy + s}`
    + ` C ${cx} ${cy + k} ${cx - k} ${cy} ${cx - s} ${cy}`
    + ` C ${cx - k} ${cy} ${cx} ${cy - k} ${cx} ${cy - s} Z`;
}

// Tiny generic crown glyph (no trademark) centred at (cx,cy), width s.
function crownPath(cx, cy, s) {
  const half = s / 2;
  const top = cy - s * 0.42;
  const dip = cy - s * 0.02;
  const base = cy + s * 0.32;
  return `M ${cx - half} ${base}`
    + ` L ${cx - half} ${top}`
    + ` L ${cx - half * 0.4} ${dip}`
    + ` L ${cx} ${top - s * 0.14}`
    + ` L ${cx + half * 0.4} ${dip}`
    + ` L ${cx + half} ${top}`
    + ` L ${cx + half} ${base} Z`;
}

// Vertical rail of rotated tiny holo text inside the bleed margin on one edge.
// side = -1 (left, reads bottom-to-top) or +1 (right, reads top-to-bottom).
function SideRail({ cx, cy, label, side, fill }) {
  const rot = side < 0 ? -90 : 90;
  return (
    <g transform={`translate(${cx} ${cy}) rotate(${rot})`}>
      <text x={0} y={0} fill={fill} fontSize={5.5} fontWeight="800" letterSpacing={2.2} textAnchor="middle" opacity={0.82}>
        {label}
      </text>
    </g>
  );
}

// "HOLO PASS" — a premium, iridescent all-access event pass frame. Sibling of
// VipFrame, dialled up to a rainbow foil: a per-instance multi-stop holo gradient
// (violet -> blue -> teal -> gold -> pink) drives an edge-to-edge double border
// plus an offset dashed shimmer pass. Right-angle holo corner brackets at all
// four corners, a lanyard slot poking into the top bleed, VIP / ALL ACCESS top-
// left, INVITATION ONLY top-right with a spark, rotated side rails, a crowned
// holo seal bottom-left, a "GOOD NIGHTS." tagline bottom-center, and a PASS NO.
// barcode bottom-right. Every label hugs the border; the centre stays clear.
export default function HoloPassFrame({ w, h, radius = 12, accent }) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `hp${++_gidc}_`;
  const g = idRef.current;

  const W = w + BLEED * 2;
  const H = h + BLEED * 2;
  const x = BLEED;
  const y = BLEED;
  const r = radius + 1;

  const holo = `url(#${g}holo)`;
  const holo2 = `url(#${g}holo2)`;
  const bright = `url(#${g}bright)`;

  const left = x;
  const right = x + w;
  const top = y;
  const bottom = y + h;
  const cx = x + w / 2;

  // Faux pass-code barcode, bottom-right, sitting in the bleed below the edge.
  const codeBars = [1.6, 0.8, 1.2, 0.6, 2, 0.8, 1, 1.4, 0.6, 1.8, 0.8, 1.2];
  const barGap = 1.3;
  const barH = 8;
  const barW = codeBars.reduce((a, b) => a + b + barGap, 0);
  const barX0 = right - barW - 26;
  const barY = bottom + 5;
  let barCursor = 0;

  return (
    <div style={{ position: 'absolute', top: -BLEED, left: -BLEED, width: W, height: H, pointerEvents: 'none' }}>
      <svg width={W} height={H} xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* Iridescent foil: violet -> blue -> teal -> gold -> pink */}
          <linearGradient id={`${g}holo`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#c4a4ff" />
            <stop offset="0.25" stopColor="#84baff" />
            <stop offset="0.5" stopColor="#8df0e2" />
            <stop offset="0.75" stopColor="#f0d99a" />
            <stop offset="1" stopColor="#ffb0dc" />
          </linearGradient>
          {/* Offset shimmer hue for the second / dashed pass */}
          <linearGradient id={`${g}holo2`} x1="1" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#8df0e2" />
            <stop offset="0.33" stopColor="#ffb0dc" />
            <stop offset="0.66" stopColor="#c4a4ff" />
            <stop offset="1" stopColor="#84baff" />
          </linearGradient>
          {/* Brighter horizontal cycle for brackets / wordmarks */}
          <linearGradient id={`${g}bright`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#c4a4ff" />
            <stop offset="0.33" stopColor="#84baff" />
            <stop offset="0.66" stopColor="#8df0e2" />
            <stop offset="1" stopColor="#ffb0dc" />
          </linearGradient>
        </defs>

        {/* outer faint holo glow */}
        <rect x={x - 2} y={y - 2} width={w + 4} height={h + 4} rx={r + 2} ry={r + 2} fill="none" stroke={holo} strokeWidth={0.8} opacity={0.3} />

        {/* refined double iridescent border */}
        <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="none" stroke={holo} strokeWidth={1.6} opacity={0.95} />
        <rect x={x + 3.5} y={y + 3.5} width={w - 7} height={h - 7} rx={Math.max(2, r - 3)} ry={Math.max(2, r - 3)} fill="none" stroke={holo} strokeWidth={0.6} opacity={0.5} />

        {/* offset dashed shimmer pass */}
        <rect x={x + 5.5} y={y + 5.5} width={w - 11} height={h - 11} rx={Math.max(2, r - 5)} ry={Math.max(2, r - 5)} fill="none" stroke={holo2} strokeWidth={0.7} opacity={0.45} strokeDasharray="5 7" />

        {/* right-angle holo corner brackets (bright double line, all four) */}
        {/* TL */}
        <path d={`M ${left - 3} ${top + 28} L ${left - 3} ${top + 8} Q ${left - 3} ${top - 3} ${top + 8} ${top - 3} L ${left + 28} ${top - 3}`} fill="none" stroke={bright} strokeWidth={2.4} strokeLinecap="round" />
        <path d={`M ${left + 4} ${top + 28} L ${left + 4} ${top + 12} Q ${left + 4} ${top + 4} ${left + 12} ${top + 4} L ${left + 28} ${top + 4}`} fill="none" stroke={holo2} strokeWidth={0.8} opacity={0.7} />
        {/* TR */}
        <path d={`M ${right - 28} ${top - 3} L ${right + 3} ${top - 3} Q ${right + 3} ${top - 3} ${right + 3} ${top + 8} L ${right + 3} ${top + 28}`} fill="none" stroke={bright} strokeWidth={2.4} strokeLinecap="round" />
        <path d={`M ${right - 28} ${top + 4} L ${right - 12} ${top + 4} Q ${right - 4} ${top + 4} ${right - 4} ${top + 12} L ${right - 4} ${top + 28}`} fill="none" stroke={holo2} strokeWidth={0.8} opacity={0.7} />
        {/* BL */}
        <path d={`M ${left - 3} ${bottom - 28} L ${left - 3} ${bottom + 8} Q ${left - 3} ${bottom + 3} ${left + 8} ${bottom + 3} L ${left + 28} ${bottom + 3}`} fill="none" stroke={bright} strokeWidth={2.4} strokeLinecap="round" />
        <path d={`M ${left + 4} ${bottom - 28} L ${left + 4} ${bottom - 6} Q ${left + 4} ${bottom - 4} ${left + 12} ${bottom - 4} L ${left + 28} ${bottom - 4}`} fill="none" stroke={holo2} strokeWidth={0.8} opacity={0.7} />
        {/* BR */}
        <path d={`M ${right - 28} ${bottom + 3} L ${right + 3} ${bottom + 3} Q ${right + 3} ${bottom + 3} ${right + 3} ${bottom - 8} L ${right + 3} ${bottom - 28}`} fill="none" stroke={bright} strokeWidth={2.4} strokeLinecap="round" />
        <path d={`M ${right - 28} ${bottom - 4} L ${right - 12} ${bottom - 4} Q ${right - 4} ${bottom - 4} ${right - 4} ${bottom - 6} L ${right - 4} ${bottom - 28}`} fill="none" stroke={holo2} strokeWidth={0.8} opacity={0.7} />

        {/* TOP-CENTER lanyard slot: notch dip in border + pill hole in the bleed */}
        <path d={`M ${cx - 30} ${top} Q ${cx - 18} ${top} ${cx - 16} ${top - 7} Q ${cx - 10} ${top - 15} ${cx} ${top - 15} Q ${cx + 10} ${top - 15} ${cx + 16} ${top - 7} Q ${cx + 18} ${top} ${cx + 30} ${top}`} fill="#070709" stroke={bright} strokeWidth={1.3} />
        <rect x={cx - 13} y={top - 11.5} width={26} height={6.5} rx={3.25} fill="#0a0a0c" stroke={bright} strokeWidth={1.1} />

        {/* TOP-LEFT: VIP wordmark | ALL ACCESS */}
        <text x={x + 36} y={y - 3.5} fill={bright} fontSize={9.5} fontWeight="800" letterSpacing={1} textAnchor="end">VIP</text>
        <line x1={x + 40} y1={y - 11.5} x2={x + 40} y2={y - 3} stroke={holo2} strokeWidth={0.7} opacity={0.7} />
        <text x={x + 44} y={y - 3.5} fill={holo2} fontSize={5.5} fontWeight="700" letterSpacing={2}>ALL ACCESS</text>

        {/* TOP-RIGHT: INVITATION ONLY + non-transferable + divider + spark */}
        <text x={x + w - 18} y={y - 7.5} fill={bright} fontSize={6} fontWeight="800" letterSpacing={1.4} textAnchor="end">INVITATION ONLY</text>
        <text x={x + w - 18} y={y - 1.5} fill={holo2} fontSize={4.3} fontWeight="700" letterSpacing={1} textAnchor="end" opacity={0.85}>MEMBERSHIP NON-TRANSFERABLE</text>
        <line x1={x + w - 90} y1={y - 5.5} x2={x + w - 18} y2={y - 5.5} stroke={holo2} strokeWidth={0.5} opacity={0.5} />
        <path d={sparkPath(x + w - 8, y - 7, 3.2)} fill={bright} />

        {/* LEFT + RIGHT vertical edge rails inside the bleed */}
        <SideRail cx={x - 7} cy={y + h / 2} label="EXCLUSIVE . ACCESS LEVEL" side={-1} fill={holo} />
        <SideRail cx={x + w + 7} cy={y + h / 2} label="NIGHT IS YOURS" side={1} fill={holo} />

        {/* BOTTOM-LEFT: holo seal — two rings + crown + VIP */}
        <g transform={`translate(${x + 11} ${bottom + 7})`}>
          <circle cx={0} cy={0} r={10} fill="#0a0a0c" />
          <circle cx={0} cy={0} r={10} fill="none" stroke={bright} strokeWidth={1} />
          <circle cx={0} cy={0} r={7} fill="none" stroke={holo2} strokeWidth={0.5} opacity={0.6} />
          <path d={crownPath(0, -0.6, 7)} fill="none" stroke={bright} strokeWidth={0.8} />
          <text x={0} y={6.4} fill={bright} fontSize={3.6} fontWeight="800" letterSpacing={0.6} textAnchor="middle">VIP</text>
        </g>

        {/* BOTTOM-CENTER: tagline with spark separators */}
        <text x={cx} y={y + h + 8.5} fill={holo} fontSize={5.3} fontWeight="700" letterSpacing={1.2} textAnchor="middle" opacity={0.88}>
          GOOD NIGHTS . GOOD PEOPLE . GOOD MUSIC.
        </text>

        {/* BOTTOM-RIGHT: PASS NO. + faux code + holo barcode */}
        <text x={x + w - 4} y={y + h + 2} fill={holo2} fontSize={4.5} fontWeight="800" letterSpacing={0.7} textAnchor="end" opacity={0.85}>
          PASS NO. V23 . 0725 . X
        </text>
        {codeBars.map((bw, i) => {
          const bx = barX0 + barCursor;
          barCursor += bw + barGap;
          return <rect key={`b${i}`} x={bx} y={barY} width={bw} height={barH} fill={bright} opacity={0.8} />;
        })}
      </svg>
    </div>
  );
}
